import { ethers } from "ethers";
import type { JoinSplitNote, StoredJoinSplitNote } from "../joinsplit/types";
import { assertFlatJoinSplitV2Note } from "./notes";
import { withDeadline } from "../utils/withDeadline";

/** Include spent-marked ciphertext: only chain nullifiers establish spent state. */
export async function fetchProtectedFlatInbox(params: {
  baseUrl: string;
  recipientKey: string;
  headers: Record<string, string>;
}): Promise<Record<string, unknown>[]> {
  return withDeadline(async (signal) => {
    const notes: Record<string, unknown>[] = [];
    const cursors = new Set<string>();
    let cursor: string | undefined;
    do {
      if (cursor && (!/^\d+$/.test(cursor) || cursors.has(cursor)))
        throw new Error("Invalid protected inbox pagination");
      if (cursor) cursors.add(cursor);
      if (cursors.size >= 100)
        throw new Error("Protected inbox pagination exceeded limit");
      const url = `${params.baseUrl}/api/v1/notes/protected/${params.recipientKey}`;
      const response = await fetch(
        cursor ? `${url}?cursor=${encodeURIComponent(cursor)}` : url,
        { headers: params.headers, signal },
      );
      if (!response.ok)
        throw new Error(
          `Protected inbox export unavailable (${response.status})`,
        );
      const result = await response.json();
      if (
        result?.success !== true ||
        !Array.isArray(result.notes) ||
        result.notes.length > 100 ||
        result.notes.some(
          (note: unknown) =>
            !note || typeof note !== "object" || Array.isArray(note),
        ) ||
        (result.nextCursor != null && typeof result.nextCursor !== "string")
      )
        throw new Error("Malformed protected inbox export");
      notes.push(...result.notes);
      cursor = result.nextCursor || undefined;
    } while (cursor);
    return notes;
  }, "Balance recovery is taking too long. Please try again.");
}

export interface FlatInboxCandidate {
  enc: { deliveryId?: string; storageClass?: string; commitment?: string };
  note: JoinSplitNote;
}

/** Before deduplication, prove the decrypted note belongs to this wallet.
 * Public inbox keys let strangers encrypt arbitrary plaintext: AEAD success
 * alone must not let a later garbage candidate replace a valid local note.
 */
export async function validateFlatInboxCandidate(
  candidate: FlatInboxCandidate,
  scope: { chainId: number; poolAddress: string; mpk: bigint },
): Promise<void> {
  await assertFlatJoinSplitV2Note({
    note: candidate.note,
    chainId: scope.chainId,
    poolAddress: scope.poolAddress,
    expectedMpk: scope.mpk,
  });
  const outer = candidate.enc.commitment;
  if (
    outer &&
    (!/^(0x)?[0-9a-fA-F]{64}$/.test(outer) ||
      ethers
        .toBeHex(BigInt(outer.startsWith("0x") ? outer : `0x${outer}`), 32)
        .toLowerCase() !== candidate.note.commitment.toLowerCase())
  ) {
    throw new Error(
      "Inbox envelope commitment differs from the authenticated note",
    );
  }
}

/** Only canonically included, unspent, wallet-owned notes enter protected storage. */
export async function protectValidatedFlatInbox(params: {
  baseUrl: string;
  recipientKey: string;
  headers: Record<string, string>;
  candidates: FlatInboxCandidate[];
  validated: ReadonlyMap<string, StoredJoinSplitNote>;
  scope: { chainId: number; poolAddress: string; mpk: bigint };
  isCurrent?: () => boolean;
  signal?: AbortSignal;
}): Promise<number> {
  let promoted = 0;
  const seen = new Set<string>();
  for (const candidate of params.candidates) {
    if (
      candidate.enc.storageClass !== "pending" ||
      !candidate.enc.deliveryId ||
      seen.has(candidate.enc.deliveryId)
    )
      continue;
    await validateFlatInboxCandidate(candidate, params.scope);
    const validated = params.validated.get(
      candidate.note.commitment.toLowerCase(),
    );
    if (!validated || validated.spent || validated.note.leafIndex < 0) continue;
    await assertFlatJoinSplitV2Note({
      note: validated.note,
      chainId: params.scope.chainId,
      poolAddress: params.scope.poolAddress,
      expectedMpk: params.scope.mpk,
    });
    try {
      // A matching commitment alone does not authenticate plaintext token
      // metadata. Bind the exact candidate to the canonically checked domain.
      await assertFlatJoinSplitV2Note({
        note: candidate.note,
        chainId: params.scope.chainId,
        poolAddress: params.scope.poolAddress,
        expectedMpk: params.scope.mpk,
        tokenAddress: validated.note.tokenData.tokenAddress,
        expectedTokenHash: validated.note.tokenHash,
      });
      if (
        candidate.note.treeIndex !== validated.note.treeIndex ||
        candidate.note.treeKey !== validated.note.treeKey
      )
        continue;
    } catch {
      continue;
    }
    if (params.isCurrent && !params.isCurrent())
      throw new Error("Wallet changed during inbox protection");
    const controller = new AbortController();
    const cancel = () => controller.abort();
    if (params.signal?.aborted) cancel();
    params.signal?.addEventListener("abort", cancel, { once: true });
    const timer = setTimeout(cancel, 30_000);
    try {
      const response = await fetch(`${params.baseUrl}/api/v1/notes/promote`, {
        method: "POST",
        signal: controller.signal,
        headers: { "Content-Type": "application/json", ...params.headers },
        body: JSON.stringify({
          recipient: params.recipientKey,
          deliveryId: candidate.enc.deliveryId,
        }),
      });
      if (!response.ok || (await response.json())?.success !== true) {
        throw new Error(
          "Protected inbox storage has not acknowledged this validated note",
        );
      }
    } finally {
      clearTimeout(timer);
      params.signal?.removeEventListener("abort", cancel);
    }
    if (params.isCurrent && !params.isCurrent())
      throw new Error("Wallet changed during inbox protection");
    seen.add(candidate.enc.deliveryId);
    promoted += 1;
  }
  return promoted;
}
